import React, { useState } from "react";
import {
  Box,
  Stack,
  Card,
  CardHeader,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Chip,
  Tooltip,
  IconButton,
  LinearProgress,
  Typography,
  SvgIcon,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useDispatch, useSelector } from "react-redux";
import { submitPromotion } from "../../../store/Slices/promotionSlice";

// Simple TikTok SVG icon
function TikTokIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 48 48">
      <path d="M33 13.5c2.2 1.7 4.9 2.7 7.8 2.9v5.7c-2.9-.1-5.7-1-7.8-2.3v10.2c0 7.1-5.7 12.8-12.8 12.8S7.4 36.1 7.4 29c0-7.1 5.7-12.8 12.8-12.8 1 0 2 .1 3 .4v6.2a7.1 7.1 0 00-3-.7c-3.9 0-7.1 3.2-7.1 7.1 0 3.9 3.2 7.1 7.1 7.1s7.1-3.2 7.1-7.1V6.6h6.7c.1 2.7 1.1 5.2 3 6.9z" />
    </SvgIcon>
  );
}

const TYPES = [
  { value: "deposit", label: "Deposit (7d)", reward: 2 },
  { value: "withdrawal", label: "Withdrawal (7d)", reward: 2 },
  { value: "security_support", label: "Security & Support (7d)", reward: 2 },
  { value: "ai_trading", label: "AI Trading (7d)", reward: 3 },
  { value: "general", label: "General (daily)", reward: 1 },
];

const PLATFORMS = [
  { value: "youtube", label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
];

// Meta for platform icons
const PLATFORM_META = {
  youtube: {
    label: "YouTube",
    icon: <YouTubeIcon sx={{ fontSize: 20, color: "error.main" }} />,
  },
  instagram: {
    label: "Instagram",
    icon: <InstagramIcon sx={{ fontSize: 20, color: "#E1306C" }} />,
  },
  tiktok: {
    label: "TikTok",
    icon: <TikTokIcon sx={{ fontSize: 20 }} />,
  },
};

const templates = {
  deposit: {
    en:
      "📥 Deposit easily with 1CryptoX — fast & secure. #1CryptoX #CryptoDeposit #SpotTrading #CryptoExchange",
    ar:
      "📥 اكتشف سهولة الإيداع في 1CryptoX — المنصة الأسرع والأكثر أمانًا للتداول. #1CryptoX #CryptoDeposit #SpotTrading #CryptoExchange",
  },
  withdrawal: {
    en:
      "💸 Withdraw instantly with 1CryptoX — secure & reliable. #1CryptoX #CryptoWithdrawal #InstantTransfer #SpotWallet",
    ar:
      "💸 اسحب أرباحك فورًا مع 1CryptoX — معاملات آمنة وسريعة. #1CryptoX #CryptoWithdrawal #InstantTransfer #SpotWallet",
  },
  security_support: {
    en:
      "🔒 1CryptoX — trusted security and 24/7 support. #1CryptoX #CryptoSecurity #SupportTeam #SafeTrading",
    ar:
      "🔒 1CryptoX — الأمان أولًا ودعم مباشر على مدار الساعة. #1CryptoX #CryptoSecurity #SupportTeam #SafeTrading",
  },
  ai_trading: {
    en:
      "🤖 Trade smarter with 1CryptoX AI Trading. #1CryptoX #AITrading #CryptoAI #SmartInvesting",
    ar:
      "🤖 تداول بذكاء مع 1CryptoX AI — أرباح تلقائية. #1CryptoX #AITrading #CryptoAI #SmartInvesting",
  },
  general: {
    en:
      "🌍 Try 1CryptoX — Spot/Futures/AI in one place. #1CryptoX #CryptoTrading #LuckyWheel #Futures #Spot",
    ar:
      "🌍 جرّب 1CryptoX — Spot/Futures/AI في مكان واحد. #1CryptoX #CryptoTrading #LuckyWheel #Futures #Spot",
  },
};

export default function PromoteForm() {
  const dispatch = useDispatch();
  const { submitting, submitError } = useSelector((s) => s.promotion);

  const [payload, setPayload] = useState({
    type: "deposit",
    platform: "youtube",
    video_url: "",
    description_text: "",
    screenshot: null,
  });
  const [lang, setLang] = useState("en");

  const copyTemplate = () => {
    const t = templates[payload.type]?.[lang] || "";
    navigator.clipboard.writeText(t);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!payload.screenshot) return alert("Please upload a screenshot");
    dispatch(submitPromotion(payload)).then((r) => {
      if (!r.error) {
        setPayload((p) => ({
          ...p,
          video_url: "",
          description_text: "",
          screenshot: null,
        }));
      }
    });
  };

  return (
    <Card
      id="promote-form"
      sx={{ borderRadius: 3, boxShadow: 6, bgcolor: "background.paper" }}
    >
      <CardHeader
        title="Promote & Earn"
        subheader="Submit your short video and get rewarded in USDT"
      />
      <CardContent>
        <Stack spacing={2} component="form" onSubmit={onSubmit}>
          {/* Topic with reward on the right */}
          <TextField
            select
            label="Video Topic"
            value={payload.type}
            onChange={(e) =>
              setPayload((p) => ({ ...p, type: e.target.value }))
            }
            fullWidth
          >
            {TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Typography>{t.label}</Typography>
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    +{t.reward} USDT
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {/* Platform with icons */}
          <TextField
            select
            label="Platform"
            value={payload.platform}
            onChange={(e) =>
              setPayload((p) => ({ ...p, platform: e.target.value }))
            }
            fullWidth
            SelectProps={{
              renderValue: (val) => {
                const meta = PLATFORM_META[val];
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {meta?.icon}
                    <span>{meta?.label ?? val}</span>
                  </Box>
                );
              },
            }}
          >
            {PLATFORMS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {PLATFORM_META[p.value]?.icon}
                  <span>{p.label}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* URL */}
          <TextField
            label="Video URL"
            placeholder="https://..."
            value={payload.video_url}
            onChange={(e) =>
              setPayload((p) => ({ ...p, video_url: e.target.value }))
            }
            fullWidth
          />

          {/* Description + copy template */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Description / Hashtags"
              value={payload.description_text}
              onChange={(e) =>
                setPayload((p) => ({
                  ...p,
                  description_text: e.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
            />
            <Stack spacing={1} alignItems="center">
              <TextField
                select
                size="small"
                label="Template Lang"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                sx={{ width: 160 }}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ar">Arabic</MenuItem>
              </TextField>
              <Tooltip title="Copy template text">
                <IconButton onClick={copyTemplate}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Upload */}
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            Upload Screenshot
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) =>
                setPayload((p) => ({
                  ...p,
                  screenshot: e.target.files?.[0],
                }))
              }
            />
          </Button>

          {/* Uploaded file chip */}
          {payload.screenshot && (
            <Chip
              label={payload.screenshot.name}
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
            />
          )}

          {/* Submit */}
          <Box>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                bgcolor: "brand.main",
                "&:hover": { bgcolor: "brand.dark" },
                borderRadius: 2,
              }}
              disabled={submitting}
            >
              Submit
            </Button>
          </Box>

          {/* Status */}
          {submitting && <LinearProgress />}
          {submitError && (
            <Typography color="error" variant="body2">
              {submitError}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
